/**
 * TagsSection - A searchable, multi-column tag selection component
 *
 * Features:
 * - Real-time search filtering of tags from API
 * - 3-column responsive tag display with standardized colors
 * - Persistent selection state across search/filtering
 * - Uses useTagsCompatibilityDisplay hook to normalize inconsistent API color values
 * - Form integration with FormGrid layout system
 *
 * Note: Search functionality will be refined in upcoming ticket.
 * This implementation is just a template to expand upon.
 *
 * Note: Form colors saving happens in the FE with a CONSTANT object
 * that could be modified in upcoming tickets to make sure the values
 * save the new ones.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
  Input,
  Button,
  ButtonType,
  Checkbox
} from 'syngenta-digital-cropwise-react-ui-kit';

import { FormValues } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import PlusIcon from 'assets/plus.svg?react';
import {
  COL_SPAN_FULL,
  COL_SPAN_ONE_SIXTH
} from 'components/FormGrid/constants';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import useTagsData from 'hooks/useTagsData';
import useTagsCompatibilityDisplay from 'hooks/useTagsCompatibilityDisplay';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';

import EditIcon from 'assets/edit_outlined.svg?react';
import NoTagsIcon from 'assets/no_tags_found.svg';
import NoSearchResultsIcon from 'assets/no_search_results_found.svg';
import TagManager from 'components/TagManager';
import { isSelectKey } from 'components/TagManager/helpers/keyboardUtils';
import SortDropdown, {
  SORT_TYPES,
  SortTypeValue
} from './components/SortDropdown';
import './index.css';

// Color priority order for sorting
const COLOR_SORT_ORDER = [
  'pink',
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'purple'
];

// Precomputed map for O(1) color priority lookups
const COLOR_PRIORITY_MAP = new Map(
  COLOR_SORT_ORDER.map((color, index) => [color, index])
);

// Form field name constant
const TAG_IDS_FIELD = 'tagIds' as const;

interface TagsSectionProps {
  excludeFields?: string[];
}

export interface Tag {
  id: string;
  name: string;
  color: string;
  description?: string;
  backgroundColor: string;
  textColor: string;
}

export interface UseTagsData {
  tags: Tag[];
  loading: boolean;
  getTags: () => void;
}

const TagsSection: React.FC<TagsSectionProps> = ({ excludeFields = [] }) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const { tags, loading, getTags } = useTagsData() as UseTagsData;

  const enhancedTags = useTagsCompatibilityDisplay(tags);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    new Set(values[TAG_IDS_FIELD] || [])
  );
  const [currentSort, setCurrentSort] = useState<SortTypeValue>(
    SORT_TYPES.NAME_ASC
  );
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);

  // Sync selectedTagIds with Formik values when form values change
  useEffect(() => {
    const formTagIds = values[TAG_IDS_FIELD];
    setSelectedTagIds(new Set(formTagIds || []));
  }, [values]);

  // Sort tags function
  const getColorSortPriority = (colorLabel: string): number => {
    // Extract color name from label (e.g., 'pink-20' -> 'pink')
    const colorName = colorLabel.split('-')[0];
    const priority = COLOR_PRIORITY_MAP.get(colorName);
    return priority ?? COLOR_SORT_ORDER.length;
  };

  const handleSortChange = (sortType: SortTypeValue) => {
    setCurrentSort(sortType);
  };

  const handleManage = () => {
    setIsTagManagerOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) {
      return enhancedTags;
    }
    return enhancedTags.filter(tag =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [enhancedTags, searchTerm]);

  const availableTags = useMemo(() => {
    const sorted = [...filteredTags];

    switch (currentSort) {
      case SORT_TYPES.NAME_ASC:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case SORT_TYPES.NAME_DESC:
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case SORT_TYPES.COLOR:
        return sorted.sort((a, b) => {
          const colorA = getColorSortPriority(a.colorLabel);
          const colorB = getColorSortPriority(b.colorLabel);

          if (colorA !== colorB) {
            return colorA - colorB;
          }

          // Secondary sort: alphabetical A to Z
          return a.name.localeCompare(b.name);
        });
      default:
        return sorted;
    }
  }, [filteredTags, currentSort]);

  const distributeTagsInColumns = (tagObjects: typeof enhancedTags) => {
    const totalTags = tagObjects.length;
    const basePerColumn = Math.floor(totalTags / 3);
    const remainder = totalTags % 3;

    const col1Count = basePerColumn + (remainder > 0 ? 1 : 0);
    const col2Count = basePerColumn + (remainder > 1 ? 1 : 0);

    const column1 = tagObjects.slice(0, col1Count);
    const column2 = tagObjects.slice(col1Count, col1Count + col2Count);
    const column3 = tagObjects.slice(col1Count + col2Count);

    return [column1, column2, column3];
  };

  const handleTagChange = (tagId: string, checked: boolean) => {
    setSelectedTagIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(tagId);
      } else {
        newSet.delete(tagId);
      }

      // Update Formik form values
      const newTagsArray = Array.from(newSet);
      setFieldValue(TAG_IDS_FIELD, newTagsArray);

      return newSet;
    });
  };

  const handleTagKeyDown = (
    e: React.KeyboardEvent,
    tagId: string,
    isChecked: boolean
  ) => {
    if (isSelectKey(e.key)) {
      e.preventDefault();
      handleTagChange(tagId, !isChecked);
    }
  };

  const renderTagColumn = (tagObjects: typeof enhancedTags) => (
    <div className="flex flex-col gap-2">
      {tagObjects.map(tag => {
        const isChecked = selectedTagIds.has(tag.id);
        return (
          <div key={tag.id} className="max-w-full min-w-0">
            <button
              data-testid={`tag-button-${tag.name}`}
              type="button"
              className="flex items-center gap-2 border-0 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 cursor-pointer bg-transparent p-0 rounded max-w-full min-w-0"
              aria-pressed={isChecked}
              aria-label={`${isChecked ? 'Deselect' : 'Select'} ${
                tag.name
              } tag`}
              onKeyDown={e => handleTagKeyDown(e, tag.id, isChecked)}
              onClick={() => handleTagChange(tag.id, !isChecked)}
            >
              <Checkbox
                checked={isChecked}
                onChange={() => {}}
                tabIndex={-1}
                className="flex-shrink-0"
              />
              <div
                className="text-sm px-2 text-left flex items-center min-w-0 max-w-full"
                style={{
                  backgroundColor: tag.backgroundColor,
                  color: tag.textColor,
                  height: '20px',
                  borderRadius: '4px',
                  fontWeight: 600
                }}
                title={tag.name}
              >
                <span className="tag-no-wrap">{tag.name}</span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );

  const formRows: FormRowConfig[] = [
    {
      id: 'tags-search-and-actions-row',
      fields: [
        {
          id: 'tags-search-input',
          colSpan: 20,
          render: () => (
            <div style={{ paddingRight: '8px' }}>
              <Input
                name="tags-search-input"
                allowClear
                placeholder={LayoutStrings.placeholders.searchTags}
                className="w-full search-input-container"
                prefixCls="syt-antd-input"
                size="middle"
                value={searchTerm}
                onChange={handleSearchChange}
                disabled={loading}
                prefix={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.2239 10.6914H11.8164L15.5589 14.4414L14.4414 15.5589L10.6914 11.8164V11.2239L10.4889 11.0139C9.63391 11.7489 8.52391 12.1914 7.31641 12.1914C4.62391 12.1914 2.44141 10.0089 2.44141 7.31641C2.44141 4.62391 4.62391 2.44141 7.31641 2.44141C10.0089 2.44141 12.1914 4.62391 12.1914 7.31641C12.1914 8.52391 11.7489 9.63391 11.0139 10.4889L11.2239 10.6914ZM3.94141 7.31641C3.94141 9.18391 5.44891 10.6914 7.31641 10.6914C9.18391 10.6914 10.6914 9.18391 10.6914 7.31641C10.6914 5.44891 9.18391 3.94141 7.31641 3.94141C5.44891 3.94141 3.94141 5.44891 3.94141 7.31641Z"
                      fill="#696F88"
                    />
                  </svg>
                }
              />
            </div>
          )
        },
        {
          id: 'tags-action-buttons',
          colSpan: COL_SPAN_ONE_SIXTH,
          render: () => (
            <div className="flex gap-2 justify-end">
              <SortDropdown
                currentSort={currentSort}
                onSortChange={handleSortChange}
                disabled={loading}
              />
              <Button
                size="middle"
                type={ButtonType.outline}
                onClick={handleManage}
                disabled={loading}
              >
                <div className="flex items-center gap-1">
                  <EditIcon width="18" height="18" />
                  <span>{LayoutStrings.buttons.manage}</span>
                </div>
              </Button>
            </div>
          )
        }
      ]
    },
    {
      id: 'tags-display-row',
      fields: [
        {
          id: 'tags-grid-columns',
          colSpan: COL_SPAN_FULL,
          render: () => {
            if (availableTags.length === 0) {
              // Show EmptyTableState for search results
              if (searchTerm) {
                return (
                  <div className="-mt-1 search-empty-state-no-padding">
                    <EmptyTableState
                      title={LayoutStrings.tags.noSearchResultsTitle}
                      message={LayoutStrings.tags.noSearchResultsMessage}
                      overlayIcon={NoSearchResultsIcon}
                    />
                  </div>
                );
              }

              // Show simple message for no filtered tags
              return (
                <div className="-mt-1 flex items-center justify-center py-8">
                  <span className="text-gray-500">
                    {LayoutStrings.tags.noTagsAvailable}
                  </span>
                </div>
              );
            }

            const [column1, column2, column3] = distributeTagsInColumns(
              availableTags
            );

            return (
              <div className="-mt-1">
                <div className="grid grid-cols-3 gap-4">
                  <div className="pl-3">{renderTagColumn(column1)}</div>
                  <div className="pl-3">{renderTagColumn(column2)}</div>
                  <div className="pl-3">{renderTagColumn(column3)}</div>
                </div>
              </div>
            );
          }
        }
      ]
    }
  ];

  if (loading) {
    return <LoadingSpinner message={LayoutStrings.tags.loadingMessage} />;
  }

  return (
    <>
      {enhancedTags.length === 0 ? (
        <EmptyTableState
          title={LayoutStrings.tags.emptyStateTitle}
          message=" "
          containerClassName="w-full h-full pt-2 pb-6 flex flex-col justify-center items-center"
          overlayIcon={NoTagsIcon}
          button={
            <Button
              type={ButtonType.primary}
              size="large"
              icon={<PlusIcon />}
              onClick={handleManage}
            >
              {LayoutStrings.buttons.createNewTag}
            </Button>
          }
        />
      ) : (
        <FormGrid rows={formRows} excludeFields={excludeFields} />
      )}
      <TagManager
        open={isTagManagerOpen}
        onClose={() => setIsTagManagerOpen(false)}
        onTagsUpdated={getTags}
      />
    </>
  );
};

export default TagsSection;
